// ============================================================
// Craft a Brew — Recipe Builder PDF & Print Export v3.15
// Date: April 14, 2026
// Load AFTER recipe-builder.js (after recipe-builder-enhancements.js
// is fine too — they don't conflict).
//
// v3.11 changelog (Apr 14, 6th pass):
//   • YEAST TABLE HEADER WRAPPING FIX: v3.9's font shrink
//     (head 10pt → 8pt, body 9pt → 7.5pt) made the header text
//     wider in relative terms than the old column widths could
//     accommodate. "Attenuation", "Flocculation", and "Packets"
//     were wrapping to two lines ("Attenuatio/n", "Flocculatio/n",
//     "Packet/s") because 22-24mm / 18mm at 8pt bold isn't wide
//     enough. Widened:
//         Attenuation:  22 → 26mm
//         Temp Range:   24 → 22mm (shorter header, can shrink)
//         Flocculation: 24 → 28mm
//         Ferm Temp:    22 → 22mm (unchanged)
//         Packets:      18 → 20mm
//         Total fixed:  110 → 118mm
//         Variety col:  auto (gets ~42mm of ~160mm content width)
//     Yeast names like "LalBrew Nottingham Ale (11g — 5 gal)"
//     now wrap cleanly onto two lines in the variety column,
//     which looks intentional rather than broken.
//   • Same fix applied to the HTML print version via explicit
//     table layout + col widths so headers never wrap there either.
//
// (See v3.10 and earlier history below for the full trail.)
//
// Layout overview (one letter-size page, portrait):
//   1. HEADER ZONE
//      - Left: CAB round logo (PNG) + "RECIPE NAME" big slab title
//      - Below title: batch metadata line (marigold) + BJCP match line
//        (marigold) + tasting notes paragraph (black, small)
//      - Right: gold vertical-divider stat strip for customer's
//        handwritten measured values: OG / FG / ABV / IBU / SRM
//   2. EXPECTED SPECS table
//   3. FERMENTABLES table
//   4. HOPS SCHEDULE table
//   5. YEAST & FERMENTATION table
//   6. EXPECTED FERMENTATION TIMELINE (captured via html2canvas)
//   7. BREW DAY NOTES (ruled lines)
//   8. FOOTER
//
// Changelog:
//   v2.0 - Initial fresh build with element IDs, modal, dual placement
//   v2.1 - Fixed jsPDF loading race condition + sidebar button polish
//   v3.0 - FULL REDESIGN to match Aren's mockup
//   v3.1 - Chart capture + SVG logo + header spacing fixes
//   v3.2 - Logo filename fix + fallback loader + stat box redesign
//   v3.3 - Margins bumped, header restructured, stat boxes redrawn
//   v3.4 - Margin/title/font tuning pass vs mockup; stat boxes became
//          one connected strip divided into 5 cells by internal vertical
//          dividers. Print CSS got color-adjust: exact for Chrome.
//   v3.5 - Stat strip simplified per Aren (4.5" × 0.5", gold dividers only).
//   v3.6 - BJCP category IDs removed from rendered output per Kyle.
//   v3.7 - Padding/spacing: 22 → 25mm PDF margin, moved HTML print
//          page margin from @page to body padding.
//   v3.8 - PDF library loading: jsDelivr primary, cdnjs fallback,
//          actual verification of library availability, no double-alert.
//   v3.9 - Compactness pass: ~20% shrink across fonts/padding,
//          top margin 25 → 15mm, batch info gray per Aren mockup.
//   v3.10 - Stat strip moved to its own row below title to fix
//           title truncation on long recipe names.
//   v3.11 - Yeast table column widths widened to stop header text
//           from wrapping (see top of file).
// ============================================================

(function () {
  'use strict';

  // ============================================================
  // CONFIG
  // ============================================================
  var LOGO_ASSET_NAME = 'cab_circle_logoAsset 1.svg';
  var LOGO_URL = ((window.SRM_IMAGE_BASE || 'https://craftabrew.com/cdn/shop/t/38/assets/') + LOGO_ASSET_NAME).replace(/ /g, '%20');

  var BRAND = {
    marigold:     [245, 158, 11],
    marigoldDeep: [217, 119, 6],
    ink:          [61, 58, 53],
    body:         [26, 26, 26],
    muted:        [122, 117, 109],
    hairline:     [224, 220, 212],
    rowStripe:    [245, 243, 238],
    cream:        [250, 247, 240],
    white:        [255, 255, 255],
    green:        [74, 190, 94],
    chartOrange:  [245, 158, 11]
  };

  // ============================================================
  // 1. Wait for the calculator to be ready
  // ============================================================
  function ready(fn) {
    if (window.PRODUCTS && document.querySelector('.cab-rb .timeline-card')) {
      fn();
    } else {
      setTimeout(function () { ready(fn); }, 100);
    }
  }
  ready(init);

  function init() {
    try {
      injectStyles();
      injectFullSection();
      injectSidebarButton();
      console.log('[CAB] Recipe PDF/Print export v3.15 loaded.');
    } catch (e) {
      console.error('[CAB] PDF export failed to initialize:', e);
    }
  }

  // ============================================================
  // 2. Styles for buttons, section, and modal
  // ============================================================
  function injectStyles() {
    var css = [
      '.cab-rb .cab-export-section {',
      '  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%) !important;',
      '  border-radius: 14px !important;',
      '  padding: 36px 36px 40px 36px !important;',
      '  margin: 28px 0 36px 0 !important;',
      '  text-align: center !important;',
      '  box-shadow: 0 4px 14px rgba(26,26,46,0.12) !important;',
      '  display: block !important;',
      '  box-sizing: border-box !important;',
      '}',
      '.cab-rb .cab-export-section h3 {',
      '  color: #ffffff !important;',
      '  font-size: 1.3em !important;',
      '  margin: 0 0 12px 0 !important;',
      '  padding: 0 !important;',
      '  font-weight: 700 !important;',
      '  letter-spacing: 0.5px !important;',
      '  font-family: inherit !important;',
      '}',
      '.cab-rb .cab-export-section p {',
      '  color: rgba(255,255,255,0.7) !important;',
      '  font-size: 0.92em !important;',
      '  margin: 0 auto 26px auto !important;',
      '  padding: 0 !important;',
      '  max-width: 520px !important;',
      '  line-height: 1.55 !important;',
      '}',
      '.cab-rb .cab-export-buttons {',
      '  display: flex !important;',
      '  gap: 14px !important;',
      '  justify-content: center !important;',
      '  flex-wrap: wrap !important;',
      '  margin: 0 !important;',
      '  padding: 0 !important;',
      '}',
      '.cab-rb .cab-export-btn {',
      '  border: none;',
      '  border-radius: 8px !important;',
      '  padding: 13px 28px !important;',
      '  font-size: 0.95em !important;',
      '  font-weight: 700 !important;',
      '  cursor: pointer;',
      '  display: inline-flex !important;',
      '  align-items: center !important;',
      '  gap: 9px !important;',
      '  transition: all 0.2s ease !important;',
      '  font-family: inherit !important;',
      '  letter-spacing: 0.3px !important;',
      '  margin: 0 !important;',
      '}',
      '.cab-rb .cab-export-btn-primary {',
      '  background: linear-gradient(135deg, #f59e0b, #d97706) !important;',
      '  color: #1a1a2e !important;',
      '}',
      '.cab-rb .cab-export-btn-primary:hover {',
      '  transform: translateY(-1px) !important;',
      '  box-shadow: 0 4px 14px rgba(245,158,11,0.4) !important;',
      '}',
      '.cab-rb .cab-export-btn-secondary {',
      '  background: transparent !important;',
      '  color: #ffffff !important;',
      '  border: 1.5px solid rgba(255,255,255,0.35) !important;',
      '}',
      '.cab-rb .cab-export-btn-secondary:hover {',
      '  background: rgba(255,255,255,0.07) !important;',
      '  border-color: rgba(255,255,255,0.6) !important;',
      '}',
      '.cab-rb .cab-export-btn svg,',
      '.cab-rb .cab-sidebar-export-btn svg {',
      '  width: 18px !important;',
      '  height: 18px !important;',
      '  stroke: currentColor !important;',
      '  stroke-width: 2.2 !important;',
      '  fill: none !important;',
      '  flex-shrink: 0 !important;',
      '}',
      '.cab-rb .cab-export-btn svg *,',
      '.cab-rb .cab-sidebar-export-btn svg * {',
      '  fill: none !important;',
      '  stroke: currentColor !important;',
      '}',
      '.cab-rb .cab-sidebar-export {',
      '  display: flex !important;',
      '  gap: 10px !important;',
      '  margin: 14px 0 4px 0 !important;',
      '  padding: 0 !important;',
      '}',
      '.cab-rb .cab-sidebar-export-btn {',
      '  flex: 1 !important;',
      '  background: #ffffff !important;',
      '  border: 2px solid #f59e0b !important;',
      '  color: #92400e !important;',
      '  padding: 14px 16px !important;',
      '  border-radius: 10px !important;',
      '  font-size: 0.95rem !important;',
      '  font-weight: 700 !important;',
      '  cursor: pointer;',
      '  display: inline-flex !important;',
      '  align-items: center !important;',
      '  justify-content: center !important;',
      '  gap: 8px !important;',
      '  transition: all 0.2s ease !important;',
      '  font-family: inherit !important;',
      '  letter-spacing: 0.3px !important;',
      '  margin: 0 !important;',
      '}',
      '.cab-rb .cab-sidebar-export-btn:hover {',
      '  background: linear-gradient(135deg, #f59e0b, #d97706) !important;',
      '  color: #1a1a2e !important;',
      '  transform: translateY(-1px) !important;',
      '  box-shadow: 0 4px 12px rgba(245,158,11,0.3) !important;',
      '}',
      '.cab-modal-overlay {',
      '  position: fixed; top: 0; left: 0; right: 0; bottom: 0;',
      '  background: rgba(26,26,46,0.65);',
      '  display: flex; align-items: center; justify-content: center;',
      '  z-index: 100000; padding: 20px;',
      '  animation: cabFadeIn 0.18s ease;',
      '}',
      '@keyframes cabFadeIn { from { opacity: 0; } to { opacity: 1; } }',
      '.cab-modal {',
      '  background: #ffffff; border-radius: 14px;',
      '  max-width: 460px; width: 100%;',
      '  padding: 32px 32px 28px 32px;',
      '  box-shadow: 0 20px 60px rgba(0,0,0,0.3);',
      '  font-family: inherit;',
      '  animation: cabSlideUp 0.22s ease;',
      '}',
      '@keyframes cabSlideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }',
      '.cab-modal-icon {',
      '  width: 52px; height: 52px;',
      '  background: linear-gradient(135deg, #f59e0b, #d97706);',
      '  border-radius: 50%;',
      '  display: flex; align-items: center; justify-content: center;',
      '  margin: 0 auto 16px auto;',
      '}',
      '.cab-modal-icon svg {',
      '  width: 26px !important; height: 26px !important;',
      '  stroke: #ffffff !important; stroke-width: 2.2 !important; fill: none !important;',
      '}',
      '.cab-modal h3 {',
      '  font-family: Kreon, serif;',
      '  font-size: 1.45rem;',
      '  margin: 0 0 8px 0;',
      '  text-align: center; color: #1a1a2e; font-weight: 400;',
      '}',
      '.cab-modal p {',
      '  font-size: 0.92rem; color: #7a756d;',
      '  text-align: center; margin: 0 0 22px 0; line-height: 1.5;',
      '}',
      '.cab-modal label {',
      '  display: block;',
      '  font-size: 0.78rem; text-transform: uppercase; letter-spacing: 0.5px;',
      '  color: #92400e; font-weight: 700; margin-bottom: 8px;',
      '}',
      '.cab-modal input[type="text"] {',
      '  width: 100%; padding: 12px 16px;',
      '  border: 1.5px solid #e8e4dc; border-radius: 10px;',
      '  font-size: 1rem; font-family: inherit; color: #1a1a2e;',
      '  box-sizing: border-box; transition: border-color 0.15s ease;',
      '}',
      '.cab-modal input[type="text"]:focus {',
      '  outline: none; border-color: #f59e0b;',
      '}',
      '.cab-modal-actions {',
      '  display: flex; gap: 10px; margin-top: 24px;',
      '}',
      '.cab-modal-btn {',
      '  flex: 1; padding: 12px 20px;',
      '  border-radius: 8px; font-size: 0.92rem; font-weight: 700;',
      '  cursor: pointer; border: none;',
      '  transition: all 0.2s ease; font-family: inherit;',
      '  letter-spacing: 0.3px;',
      '}',
      '.cab-modal-btn-cancel { background: #f5f3ee; color: #7a756d; }',
      '.cab-modal-btn-cancel:hover { background: #e8e4dc; }',
      '.cab-modal-btn-confirm {',
      '  background: linear-gradient(135deg, #f59e0b, #d97706);',
      '  color: #1a1a2e;',
      '}',
      '.cab-modal-btn-confirm:hover {',
      '  transform: translateY(-1px);',
      '  box-shadow: 0 4px 12px rgba(245,158,11,0.4);',
      '}'
    ].join('\n');

    var style = document.createElement('style');
    style.id = 'cab-export-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ============================================================
  // 3. Inject the full section below the timeline
  // ============================================================
  function injectFullSection() {
    var timeline = document.querySelector('.cab-rb .timeline-card');
    if (!timeline || !timeline.parentNode) return;

    var section = document.createElement('div');
    section.className = 'cab-export-section';
    section.innerHTML =
      '<h3>SAVE YOUR RECIPE</h3>' +
      '<p>Download a branded PDF to reference on brew day, or print a copy for your brewing notebook. Your recipe stays saved in your browser between sessions.</p>' +
      '<div class="cab-export-buttons">' +
      '<button type="button" class="cab-export-btn cab-export-btn-primary" data-action="pdf">' +
      svgIcon('download') + 'Save as PDF' +
      '</button>' +
      '<button type="button" class="cab-export-btn cab-export-btn-secondary" data-action="print">' +
      svgIcon('printer') + 'Print Recipe' +
      '</button>' +
      '</div>';

    timeline.parentNode.insertBefore(section, timeline.nextSibling);

    section.querySelector('[data-action="pdf"]').addEventListener('click', function () {
      promptForName(generatePDF);
    });
    section.querySelector('[data-action="print"]').addEventListener('click', function () {
      promptForName(printRecipe);
    });
  }

  // ============================================================
  // 4. Inject compact buttons in the sidebar
  // ============================================================
  function injectSidebarButton() {
    var cartBtn = document.querySelector('.cab-rb #cartBtn');
    if (!cartBtn || !cartBtn.parentNode) return;

    var wrap = document.createElement('div');
    wrap.className = 'cab-sidebar-export';
    wrap.innerHTML =
      '<button type="button" class="cab-sidebar-export-btn" data-action="pdf">' +
      svgIcon('download') + 'Save PDF' +
      '</button>' +
      '<button type="button" class="cab-sidebar-export-btn" data-action="print">' +
      svgIcon('printer') + 'Print' +
      '</button>';

    cartBtn.parentNode.insertBefore(wrap, cartBtn.nextSibling);

    wrap.querySelector('[data-action="pdf"]').addEventListener('click', function () {
      promptForName(generatePDF);
    });
    wrap.querySelector('[data-action="print"]').addEventListener('click', function () {
      promptForName(printRecipe);
    });
  }

  // ============================================================
  // 5. SVG icon helper
  // ============================================================
  function svgIcon(name) {
    var icons = {
      download: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>',
      printer: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>',
      bookmark: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>'
    };
    return icons[name] || '';
  }

  // ============================================================
  // 6. Custom modal for recipe name input
  // ============================================================
  function promptForName(callback) {
    var existing = document.querySelector('.cab-modal-overlay');
    if (existing) existing.remove();

    var overlay = document.createElement('div');
    overlay.className = 'cab-modal-overlay';
    overlay.innerHTML =
      '<div class="cab-modal" role="dialog" aria-modal="true">' +
      '<div class="cab-modal-icon">' + svgIcon('bookmark') + '</div>' +
      '<h3>Name Your Recipe</h3>' +
      '<p>Give your brew a memorable name. This will appear on your saved PDF or printed copy.</p>' +
      '<label for="cab-recipe-name-input">Recipe Name</label>' +
      '<input type="text" id="cab-recipe-name-input" placeholder="e.g. Backyard Pale Ale" maxlength="80" />' +
      '<div class="cab-modal-actions">' +
      '<button type="button" class="cab-modal-btn cab-modal-btn-cancel">Cancel</button>' +
      '<button type="button" class="cab-modal-btn cab-modal-btn-confirm">Continue</button>' +
      '</div>' +
      '</div>';

    document.body.appendChild(overlay);

    var input = overlay.querySelector('#cab-recipe-name-input');
    setTimeout(function () { input.focus(); }, 50);

    function close() { overlay.remove(); }
    function submit() {
      var name = input.value.trim() || 'My Custom Brew';
      close();
      callback(name);
    }

    overlay.querySelector('.cab-modal-btn-cancel').addEventListener('click', close);
    overlay.querySelector('.cab-modal-btn-confirm').addEventListener('click', submit);

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submit(); }
      if (e.key === 'Escape') { close(); }
    });

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) close();
    });
  }

  // ============================================================
  // 7. Gather recipe data from the calculator
  // ============================================================
  function gatherRecipeData() {
    var data = {
      batchSize: val('batchSize'),
      boilSize: val('boilSize'),
      method: getSelectText('brewMethod'),
      efficiency: '',
      fermentables: [],
      hops: [],
      yeast: { name: '', attenuation: '', tempRange: '', flocculation: '' },
      fermTemp: val('fermTemp'),
      packets: getSelectText('yeastPackets'),
      starter: { active: false, size: '', method: '' },
      stats: {
        og: text('ogValue'),
        fg: text('fgValue'),
        abv: text('abvValue'),
        ibu: text('ibuValue'),
        srm: text('srmValue')
      },
      bjcpName: text('styleName'),
      bjcpId: text('styleId'),
      tastingNotes: text('flavorText'),
      timeline: {
        startSG: text('fermStartSG'),
        targetFG: text('fermEndFG'),
        finalABV: text('fermEndABV'),
        estDays: text('fermTotalDays')
      }
    };

    var effGroup = document.getElementById('efficiencyGroup');
    if (effGroup && effGroup.style.display !== 'none') {
      data.efficiency = getSelectText('efficiencySelect');
    }

    document.querySelectorAll('#fermentablesBody tr').forEach(function (row) {
      var sel = row.querySelector('.ingredient-select');
      if (!sel || !sel.value) return;
      var amtInput = row.querySelector('.amount-input');
      var unitSel = row.querySelector('.unit-select');
      var crushedInput = row.querySelector('.crushed-check input');
      var pctCell = row.querySelector('.pct-value');

      var nameOpt = sel.options[sel.selectedIndex];
      var amount = amtInput ? amtInput.value : '';
      var unit = unitSel ? unitSel.value : 'lb';

      data.fermentables.push({
        name: nameOpt ? nameOpt.text : '',
        amount: amount + ' ' + unit,
        pct: pctCell ? pctCell.textContent.trim() : '',
        crushed: crushedInput && crushedInput.checked ? 'Yes' : (crushedInput ? 'No' : '')
      });
    });

    document.querySelectorAll('#hopsBody tr').forEach(function (row) {
      var sel = row.querySelector('.ingredient-select');
      if (!sel || !sel.value) return;
      var inputs = row.querySelectorAll('.amount-input');
      var selects = row.querySelectorAll('select');
      if (inputs.length < 3) return;

      var nameOpt = sel.options[sel.selectedIndex];
      var name = nameOpt ? nameOpt.text.replace(/\s*\([\d.]+%\s*AA\)\s*$/, '') : '';

      var useText = '';
      if (selects[1]) {
        var useOpt = selects[1].options[selects[1].selectedIndex];
        useText = useOpt ? useOpt.text : '';
      }

      data.hops.push({
        name: name,
        amount: inputs[0].value + ' oz',
        aa: inputs[1].value + '%',
        use: useText,
        time: inputs[2].value + ' min'
      });
    });

    var yeastSel = document.getElementById('yeastSelect');
    if (yeastSel && yeastSel.selectedIndex > 0) {
      data.yeast.name = yeastSel.options[yeastSel.selectedIndex].text;
      data.yeast.attenuation = text('yeastAttenuation');
      data.yeast.tempRange = text('yeastTempRange');
      data.yeast.flocculation = text('yeastFlocculation');
    }

    var starterCheck = document.getElementById('useStarter');
    if (starterCheck && starterCheck.checked) {
      data.starter.active = true;
      data.starter.size = getSelectText('starterSize');
      data.starter.method = getSelectText('starterMethod');
    }

    if (data.bjcpName === 'Add ingredients...') data.bjcpName = '';
    if (data.tastingNotes === 'Add ingredients to generate a flavor profile...') data.tastingNotes = '';

    return data;
  }

  // ============================================================
  // 8. Capture the fermentation timeline chart as a PNG
  // ============================================================
  function captureTimelineChart() {
    return loadHtml2Canvas().then(function () {
      if (!window.html2canvas) return null;

      var timelineBody = document.querySelector('.cab-rb .timeline-body');
      if (!timelineBody) return null;

      var chartSvg = timelineBody.querySelector('svg');
      if (chartSvg) {
        return captureSvgAsPng(chartSvg).catch(function () {
          return captureTimelineBodyWithHiding(timelineBody);
        });
      }

      var chartCanvas = timelineBody.querySelector('canvas');
      if (chartCanvas) {
        try {
          return Promise.resolve(chartCanvas.toDataURL('image/png'));
        } catch (e) {
          // Fall through
        }
      }

      return captureTimelineBodyWithHiding(timelineBody);
    });
  }

  function captureSvgAsPng(svg) {
    return new Promise(function (resolve, reject) {
      try {
        var bbox = svg.getBoundingClientRect();
        var width = Math.max(1, Math.round(bbox.width));
        var height = Math.max(1, Math.round(bbox.height));

        var clone = svg.cloneNode(true);
        if (!clone.getAttribute('viewBox') && svg.viewBox && svg.viewBox.baseVal) {
          var vb = svg.viewBox.baseVal;
          clone.setAttribute('viewBox', vb.x + ' ' + vb.y + ' ' + vb.width + ' ' + vb.height);
        }
        clone.setAttribute('width', width);
        clone.setAttribute('height', height);
        clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

        var svgString = new XMLSerializer().serializeToString(clone);
        var svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
        var url = URL.createObjectURL(svgBlob);

        var img = new Image();
        img.onload = function () {
          var scale = 2;
          var canvas = document.createElement('canvas');
          canvas.width = width * scale;
          canvas.height = height * scale;
          var ctx = canvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0, width, height);
          URL.revokeObjectURL(url);
          try {
            resolve(canvas.toDataURL('image/png'));
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = function (err) {
          URL.revokeObjectURL(url);
          reject(err);
        };
        img.src = url;
      } catch (err) {
        reject(err);
      }
    });
  }

  function captureTimelineBodyWithHiding(timelineBody) {
    var hiddenElements = [];

    var hidingSelectors = [
      '.ferm-stats-row',
      '.ferm-note',
      '.ferm-note-text',
      '.phase-grid',
      '.phase-row',
      '.phase-cards',
      '.phase-card',
      '[class*="phase-card"]',
      '[class*="ferm-note"]',
      '[class*="ferm-stats"]',
      '[class*="phase-row"]',
      '[class*="phase-grid"]',
      '[class*="optimal"]',
      '.ferm-temp-badge'
    ];
    hidingSelectors.forEach(function (sel) {
      timelineBody.querySelectorAll(sel).forEach(function (el) {
        hiddenElements.push({ el: el, display: el.style.display });
        el.style.display = 'none';
      });
    });

    return window.html2canvas(timelineBody, {
      backgroundColor: '#ffffff',
      scale: 2,
      logging: false,
      useCORS: true
    }).then(function (canvas) {
      hiddenElements.forEach(function (item) {
        item.el.style.display = item.display;
      });
      return canvas.toDataURL('image/png');
    }).catch(function (err) {
      hiddenElements.forEach(function (item) {
        item.el.style.display = item.display;
      });
      console.warn('[CAB] Timeline chart capture failed:', err);
      return null;
    });
  }

  // ============================================================
  // 9. Load the CAB logo as a data URL
  // ============================================================
  function loadLogo() {
    var isSvg = /\.svg(\?|$)/i.test(LOGO_URL);
    if (isSvg) {
      return loadSvgLogo().then(function (result) {
        if (result) return result;
        console.warn('[CAB] SVG fetch path failed, trying raster Image fallback');
        return loadRasterLogo();
      });
    }
    return loadRasterLogo();
  }

  function loadSvgLogo() {
    return fetch(LOGO_URL, { mode: 'cors' })
      .then(function (res) {
        if (!res.ok) throw new Error('Logo fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (svgText) {
        var targetSize = 600;
        var parser = new DOMParser();
        var svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
        var svgEl = svgDoc.documentElement;

        if (!svgEl.getAttribute('viewBox')) {
          var origW = parseFloat(svgEl.getAttribute('width')) || targetSize;
          var origH = parseFloat(svgEl.getAttribute('height')) || targetSize;
          svgEl.setAttribute('viewBox', '0 0 ' + origW + ' ' + origH);
        }
        svgEl.setAttribute('width', targetSize);
        svgEl.setAttribute('height', targetSize);
        if (!svgEl.getAttribute('xmlns')) {
          svgEl.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        }

        var normalizedSvg = new XMLSerializer().serializeToString(svgEl);
        var blob = new Blob([normalizedSvg], { type: 'image/svg+xml;charset=utf-8' });
        var url = URL.createObjectURL(blob);

        return new Promise(function (resolve) {
          var img = new Image();
          img.onload = function () {
            try {
              var canvas = document.createElement('canvas');
              canvas.width = targetSize;
              canvas.height = targetSize;
              var ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0, targetSize, targetSize);
              URL.revokeObjectURL(url);
              resolve({
                dataUrl: canvas.toDataURL('image/png'),
                width: targetSize,
                height: targetSize
              });
            } catch (e) {
              URL.revokeObjectURL(url);
              console.warn('[CAB] SVG logo canvas conversion failed:', e);
              resolve(null);
            }
          };
          img.onerror = function () {
            URL.revokeObjectURL(url);
            console.warn('[CAB] SVG logo image load failed');
            resolve(null);
          };
          img.src = url;
        });
      })
      .catch(function (err) {
        console.warn('[CAB] SVG logo fetch failed:', err);
        return null;
      });
  }

  function loadRasterLogo() {
    return new Promise(function (resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        try {
          var canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve({
            dataUrl: canvas.toDataURL('image/png'),
            width: img.naturalWidth,
            height: img.naturalHeight
          });
        } catch (e) {
          console.warn('[CAB] Logo canvas conversion failed:', e);
          resolve(null);
        }
      };
      img.onerror = function () {
        console.warn('[CAB] Logo failed to load from:', LOGO_URL);
        resolve(null);
      };
      img.src = LOGO_URL;
    });
  }

  // ============================================================
  // 10. Save as PDF — v3.14 rewrite
  // --------------------------------------------------------------
  // Previously this was a ~400-line jsPDF + autoTable flow that
  // drew the recipe programmatically. It had persistent issues
  // with column width tuning and (worse) the mash water add-on
  // couldn't reliably patch jsPDF's save method to inject its
  // section — patching jsPDF.prototype.save failed because
  // jsPDF 2.x uses the API pattern, and patching jsPDF.API.save
  // also turned out unreliable in Kyle's environment.
  //
  // v3.14 pivot: Save and Print use the SAME rendering pipeline.
  // Save routes through printRecipe with asSavePdf=true, which
  // builds the HTML, renders it to an offscreen iframe, lets the
  // mash water add-on inject via _cabInjectMashWaterIntoDoc, then
  // uses html2canvas + jsPDF.addImage to produce a downloadable
  // PDF. No more autoTable, no more save-method patching, no more
  // print-vs-save layout drift.
  // ============================================================
  function generatePDF(recipeName) {
    printRecipe(recipeName, true);
  }

  // ============================================================
  // 11. Print Recipe — HTML version matching the PDF layout
  // ============================================================
  function printRecipe(recipeName, asSavePdf) {
    captureTimelineChart().then(function (chartDataUrl) {
      var data = gatherRecipeData();

      var h = [];
      h.push('<!DOCTYPE html><html><head><meta charset="UTF-8">');
      h.push('<title>' + escapeHtml(recipeName) + ' \u2014 Craft a Brew Recipe</title>');
      h.push('<link href="https://fonts.googleapis.com/css2?family=Kreon:wght@400;500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">');
      h.push('<style>');
      h.push('@page { margin: 0; size: letter; }');
      h.push('* { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }');
      h.push('html, body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }');
      h.push('body { font-family: "DM Sans", Helvetica, Arial, sans-serif; color: #1a1a1a; font-size: 9pt; line-height: 1.35; margin: 0; padding: 0.6in 1in 1in 1in; }');
      h.push('@media screen { body { background: #e8e4dc; padding: 40px 20px; } .page-inner { background: #ffffff; max-width: 8.5in; margin: 0 auto; padding: 0.6in 1in 1in 1in; box-shadow: 0 4px 20px rgba(0,0,0,0.08); min-height: 11in; } }');
      h.push('@media print { .page-inner { padding: 0; max-width: none; box-shadow: none; background: transparent; } body { padding: 0.6in 1in 1in 1in; background: transparent; } }');

      // v3.13: restructured header to match PDF v3.10 layout.
      // Previously .header was a single flex row with logo+title on
      // the left and stat strip (fixed 432px) on the right. That
      // left only ~74px for the title column, which wrapped long
      // recipe names word-by-word vertically. Now the logo+title
      // row is on its own with flex:1 title, and the stat strip
      // sits on its own row centered below.
      h.push('.header-top { display: flex; align-items: center; gap: 14px; margin-bottom: 10px; }');
      h.push('.logo-circle { width: 72px; height: 72px; border-radius: 50%; background: #f59e0b; flex-shrink: 0; display: flex; align-items: center; justify-content: center; color: #1a1a1a; font-family: "Kreon", serif; font-weight: 700; font-size: 9pt; text-align: center; line-height: 1.1; overflow: hidden; }');
      h.push('.logo-circle img { width: 100%; height: 100%; object-fit: contain; }');
      h.push('.recipe-title { flex: 1; font-family: "Kreon", serif; font-size: 24pt; font-weight: 700; margin: 0; color: #1a1a1a; line-height: 1.05; text-transform: uppercase; word-wrap: break-word; overflow-wrap: break-word; }');
      h.push('.batch-info { font-size: 8pt; color: #7a756d !important; font-weight: 700; margin: 4px 0 2px 0; }');
      h.push('.bjcp-line { font-size: 8pt; color: #f59e0b !important; font-weight: 700; margin-bottom: 4px; }');
      h.push('.tasting { font-size: 7.5pt; color: #1a1a1a; line-height: 1.45; margin: 0 0 8px 0; }');

      h.push('.stat-strip-row { display: flex; justify-content: center; margin-bottom: 10px; }');
      h.push('.header-stats { display: flex; height: 40px; width: 432px; }');
      h.push('.header-stat-box { flex: 1; padding: 3px 5px; font-size: 8pt; font-weight: 700; color: #1a1a1a; border-right: 1.5px solid #f59e0b !important; }');
      h.push('.header-stat-box:last-child { border-right: none; }');

      h.push('h2 { font-family: "Kreon", serif; font-size: 11.5pt; color: #f59e0b !important; margin: 10px 0 3px 0; padding: 0 0 1px 0; border-bottom: 1.8px solid #f59e0b !important; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }');

      /* Tables. v3.11: added table-layout:fixed and explicit col widths
         for the yeast table so headers stop wrapping in browser print. */
      h.push('table { width: 100%; border-collapse: collapse; margin-bottom: 3px; font-size: 7.5pt; }');
      h.push('th { text-align: left; padding: 4px 8px; font-weight: 700; border-bottom: 1px solid #e0dcd4; color: #1a1a1a; font-size: 8pt; white-space: nowrap; }');
      h.push('td { padding: 4px 8px; color: #1a1a1a; }');
      h.push('tr:nth-child(even) td { background: #f5f3ee !important; }');
      h.push('tr:nth-child(odd) td { background: #ffffff !important; }');
      h.push('.yeast-detail-line { font-size: 7pt; color: #7a756d; margin: 1px 0 6px 8px; }');

      h.push('.timeline-chart { width: 100%; margin-top: 4px; text-align: center; }');
      h.push('.timeline-chart img { max-width: 100%; max-height: 220px; }');

      h.push('.notes-lines { margin-top: 4px; }');
      h.push('.notes-line { border-bottom: 1.2px solid #f59e0b !important; height: 22px; }');

      h.push('.footer { margin-top: 14px; text-align: center; font-size: 6.5pt; color: #7a756d; padding-top: 6px; border-top: 0.5px solid #e0dcd4; }');

      h.push('</style></head><body>');
      h.push('<div class="page-inner">');

      // v3.13: two-row header (logo+title on row 1, stat strip on row 2)
      h.push('<div class="header-top">');
      h.push('<div class="logo-circle">');
      h.push('<img src="' + LOGO_URL + '" onerror="this.style.display=\'none\'; this.parentNode.innerHTML=\'CRAFT<br>A BREW\';" alt="Craft a Brew"/>');
      h.push('</div>');
      h.push('<div class="recipe-title">' + escapeHtml(recipeName) + '</div>');
      h.push('</div>');

      h.push('<div class="stat-strip-row">');
      h.push('<div class="header-stats">');
      var headerLabels = ['OG:', 'FG:', 'ABV:', 'IBU:', 'SRM:'];
      for (var i = 0; i < headerLabels.length; i++) {
        h.push('<div class="header-stat-box">' + headerLabels[i] + '</div>');
      }
      h.push('</div>');
      h.push('</div>');

      var batchParts = [];
      if (data.batchSize) batchParts.push('Batch: ' + data.batchSize + ' gal');
      if (data.boilSize) batchParts.push('Boil: ' + data.boilSize + ' gal');
      if (data.method) batchParts.push('Method: ' + escapeHtml(data.method));
      if (data.efficiency) batchParts.push('Efficiency: ' + escapeHtml(data.efficiency));
      if (batchParts.length) {
        h.push('<div class="batch-info">' + batchParts.join('  |  ') + '</div>');
      }

      if (data.bjcpName) {
        h.push('<div class="bjcp-line">Style Match: ' + escapeHtml(data.bjcpName) + '</div>');
      }
      if (data.tastingNotes) {
        h.push('<div class="tasting">' + escapeHtml(data.tastingNotes) + '</div>');
      }

      h.push('<h2>EXPECTED SPECS</h2>');
      h.push('<table>');
      h.push('<tr><th>Recipe</th><th>OG</th><th>FG</th><th>ABV</th><th>IBU</th><th>SRM</th></tr>');
      h.push('<tr>');
      h.push('<td><strong>' + escapeHtml(recipeName) + '</strong></td>');
      h.push('<td>' + escapeHtml(data.stats.og || '\u2014') + '</td>');
      h.push('<td>' + escapeHtml(data.stats.fg || '\u2014') + '</td>');
      h.push('<td>' + escapeHtml(data.stats.abv || '\u2014') + '</td>');
      h.push('<td>' + escapeHtml(data.stats.ibu || '\u2014') + '</td>');
      h.push('<td>' + escapeHtml(data.stats.srm || '\u2014') + '</td>');
      h.push('</tr>');
      h.push('</table>');

      if (data.fermentables.length) {
        h.push('<h2>FERMENTABLES</h2><table>');
        h.push('<tr><th>Ingredient</th><th>Amount</th><th>%</th><th>Milled</th></tr>');
        data.fermentables.forEach(function (f) {
          h.push('<tr><td>' + escapeHtml(f.name) + '</td><td>' + escapeHtml(f.amount) + '</td><td>' + escapeHtml(f.pct) + '</td><td>' + escapeHtml(f.crushed) + '</td></tr>');
        });
        h.push('</table>');
      }

      if (data.hops.length) {
        h.push('<h2>HOPS SCHEDULE</h2><table>');
        h.push('<tr><th>Variety</th><th>Amount</th><th>AA%</th><th>Use</th><th>Time</th></tr>');
        data.hops.forEach(function (hop) {
          h.push('<tr><td>' + escapeHtml(hop.name) + '</td><td>' + escapeHtml(hop.amount) + '</td><td>' + escapeHtml(hop.aa) + '</td><td>' + escapeHtml(hop.use) + '</td><td>' + escapeHtml(hop.time) + '</td></tr>');
        });
        h.push('</table>');
      }

      if (data.yeast.name) {
        // v3.11: explicit fixed layout + colgroup so headers don't wrap
        h.push('<h2>YEAST &amp; FERMENTATION</h2>');
        h.push('<table style="table-layout: fixed;">');
        h.push('<colgroup>');
        h.push('<col style="width: auto;"/>');
        h.push('<col style="width: 78px;"/>');
        h.push('<col style="width: 72px;"/>');
        h.push('<col style="width: 86px;"/>');
        h.push('<col style="width: 70px;"/>');
        h.push('<col style="width: 58px;"/>');
        h.push('</colgroup>');
        h.push('<tr><th>Variety</th><th>Attenuation</th><th>Temp Range</th><th>Flocculation</th><th>Ferm Temp</th><th>Packets</th></tr>');
        h.push('<tr>');
        h.push('<td>' + escapeHtml(data.yeast.name) + '</td>');
        h.push('<td>' + escapeHtml(data.yeast.attenuation && data.yeast.attenuation !== '\u2014' ? data.yeast.attenuation : '\u2014') + '</td>');
        h.push('<td>' + escapeHtml(data.yeast.tempRange && data.yeast.tempRange !== '\u2014' ? data.yeast.tempRange : '\u2014') + '</td>');
        h.push('<td>' + escapeHtml(data.yeast.flocculation && data.yeast.flocculation !== '\u2014' ? data.yeast.flocculation : '\u2014') + '</td>');
        h.push('<td>' + escapeHtml(data.fermTemp ? data.fermTemp + '\u00B0F' : '\u2014') + '</td>');
        h.push('<td>' + escapeHtml(data.packets || '\u2014') + '</td>');
        h.push('</tr>');
        h.push('</table>');

        var td = [];
        if (data.timeline.startSG && data.timeline.startSG !== '1.000') td.push('Start SG: ' + data.timeline.startSG);
        if (data.timeline.targetFG && data.timeline.targetFG !== '1.000') td.push('Target FG: ' + data.timeline.targetFG);
        if (data.timeline.finalABV && data.timeline.finalABV !== '0.0%') td.push('Est ABV: ' + data.timeline.finalABV);
        if (data.timeline.estDays) td.push('Est Days: ' + data.timeline.estDays);
        if (data.starter.active) td.push('Starter: ' + data.starter.size + ' (' + data.starter.method + ')');
        if (td.length) {
          h.push('<div class="yeast-detail-line">' + escapeHtml(td.join('  |  ')) + '</div>');
        }
      }

      if (chartDataUrl) {
        h.push('<h2>EXPECTED FERMENTATION TIMELINE</h2>');
        h.push('<div class="timeline-chart"><img src="' + chartDataUrl + '" alt="Fermentation timeline chart"/></div>');
      }

      h.push('<h2>BREW DAY NOTES:</h2>');
      h.push('<div class="notes-lines">');
      for (var n = 0; n < 5; n++) h.push('<div class="notes-line"></div>');
      h.push('</div>');

      h.push('<div class="footer">Generated by the Craft a Brew Recipe Builder  \u2022  craftabrew.com</div>');

      h.push('</div>');
      h.push('</body></html>');

      var fullHtml = h.join('\n');

      // v3.14: asSavePdf flag routes the same HTML to either a
      // print window (original behavior) or an offscreen iframe
      // that gets captured by html2canvas and written to a PDF
      // via jsPDF.addImage. Both paths share the SAME HTML source
      // so print and save produce identical-looking output.
      if (asSavePdf) {
        saveHtmlAsPdf(fullHtml, recipeName);
        return;
      }

      var printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Pop-up blocked. Please allow pop-ups for craftabrew.com to print your recipe.');
        return;
      }
      printWindow.document.write(fullHtml);
      printWindow.document.close();
      setTimeout(function () {
        try { printWindow.print(); } catch (e) {}
      }, 600);
    });
  }

  // ============================================================
  // 11b. saveHtmlAsPdf — new in v3.14
  // --------------------------------------------------------------
  // Takes the same HTML string that printRecipe builds, renders
  // it into an offscreen iframe, lets the mash water add-on
  // inject its section via the shared public API, captures the
  // rendered body with html2canvas, and builds a multi-page PDF
  // via jsPDF.addImage. This replaces the old autoTable-based
  // generatePDF flow entirely, which means:
  //   • Save and Print produce visually identical output
  //   • Mash water section appears in both (via the shared
  //     _cabInjectMashWaterIntoDoc hook from the mash water addon)
  //   • No more jsPDF.API.save patching — the mash water section
  //     is baked into the captured DOM before we ever touch jsPDF
  //   • Yeast table column widths, long title handling, and all
  //     other layout concerns are handled by CSS instead of
  //     autoTable cellWidth tuning
  //
  // The tradeoff is that the resulting PDF is image-based (not
  // selectable text). For a brew-day recipe sheet that's totally
  // fine — the recipe is already online in a searchable form,
  // and the printed/saved version is for the brewer to scribble
  // notes on.
  // ============================================================
  function saveHtmlAsPdf(fullHtml, recipeName) {
    // v3.15: preload logo as a data URL (bypasses CORS entirely via
    // fetch + local SVG rendering) so html2canvas can rasterize it
    // without canvas tainting. Also load jsPDF + html2canvas in
    // parallel.
    Promise.all([loadJsPDF(), loadHtml2Canvas(), loadLogo()])
      .then(function (results) {
        if (!window.html2canvas) {
          throw new Error('html2canvas failed to load');
        }
        var logoData = results[2];
        return renderHtmlToPdf(fullHtml, recipeName, logoData);
      })
      .catch(function (err) {
        console.error('[CAB] Save as PDF failed:', err);
        if (!err || !err.alreadyAlerted) {
          alert('Sorry, there was an error generating your PDF. Please try again.');
        }
      });
  }

  function renderHtmlToPdf(fullHtml, recipeName, logoData) {
    return new Promise(function (resolve, reject) {
      // Create offscreen iframe at letter-page width (8.5in = 816px)
      // Height is tall enough to comfortably fit multi-section content
      // without clipping; html2canvas captures via scrollHeight anyway.
      var iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText =
        'position: fixed; top: -10000px; left: 0; ' +
        'width: 816px; height: 2000px; ' +
        'border: 0; visibility: hidden; pointer-events: none;';
      document.body.appendChild(iframe);

      function cleanup() {
        try { if (iframe.parentNode) document.body.removeChild(iframe); }
        catch (e) { /* ignore */ }
      }

      var iframeDoc;
      try {
        iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(fullHtml);
        iframeDoc.close();
      } catch (e) {
        cleanup();
        reject(e);
        return;
      }

      // v3.15 fix #1: override the @media screen styles that make
      // the iframe render with a cream backdrop + white page-inner
      // box (intended for on-screen preview in a real print popup).
      // For PDF capture we want the flat print layout: white
      // background, page padding on body, no page-inner decoration.
      try {
        var override = iframeDoc.createElement('style');
        override.textContent =
          'html, body { background: #ffffff !important; } ' +
          'body { margin: 0 !important; padding: 0.6in 1in 1in 1in !important; } ' +
          '.page-inner { background: #ffffff !important; max-width: none !important; ' +
          'margin: 0 !important; padding: 0 !important; box-shadow: none !important; ' +
          'min-height: 0 !important; }';
        iframeDoc.head.appendChild(override);
      } catch (e) { /* best effort */ }

      // v3.15 fix #2: replace the logo img src with the preloaded
      // data URL so html2canvas can rasterize it without CORS taint.
      // The inline <img src="https://craftabrew.com/cdn/..."> would
      // normally load non-CORS and taint the canvas, leaving an
      // empty orange circle in the captured output.
      try {
        if (logoData && logoData.dataUrl) {
          var logoImg = iframeDoc.querySelector('.logo-circle img');
          if (logoImg) {
            // Remove the onerror handler so we don't accidentally
            // clobber the new src mid-swap
            logoImg.onerror = null;
            logoImg.removeAttribute('onerror');
            logoImg.src = logoData.dataUrl;
          }
        } else {
          console.warn('[CAB] Save: logo preload failed, PDF will show empty circle fallback');
        }
      } catch (e) { /* best effort */ }

      // Wait a tick for DOM + a bit for fonts/Google Fonts stylesheet
      setTimeout(function () {
        try {
          // Let the mash water add-on inject its section into the
          // iframe doc, if the add-on is loaded and the recipe is
          // all-grain. The add-on will no-op for extract recipes.
          if (typeof window._cabInjectMashWaterIntoDoc === 'function') {
            try {
              window._cabInjectMashWaterIntoDoc(iframeDoc);
            } catch (mwErr) {
              console.warn('[CAB] Mash water inject into save iframe failed:', mwErr);
            }
          }

          // Wait for fonts and images to be ready
          var fontsReady = iframeDoc.fonts && iframeDoc.fonts.ready
            ? iframeDoc.fonts.ready
            : Promise.resolve();

          var imagesReady = Promise.all(
            Array.prototype.map.call(iframeDoc.images || [], function (img) {
              if (img.complete && img.naturalWidth > 0) return Promise.resolve();
              return new Promise(function (res) {
                var done = false;
                function finish() { if (!done) { done = true; res(); } }
                img.addEventListener('load', finish);
                img.addEventListener('error', finish);
                // Safety: don't wait forever on a single image
                setTimeout(finish, 3000);
              });
            })
          );

          Promise.all([fontsReady, imagesReady]).then(function () {
            // Give layout one more frame to settle
            setTimeout(function () {
              captureAndBuildPdf(iframeDoc, recipeName)
                .then(function () { cleanup(); resolve(); })
                .catch(function (err) { cleanup(); reject(err); });
            }, 100);
          }).catch(function (err) {
            cleanup();
            reject(err);
          });
        } catch (e) {
          cleanup();
          reject(e);
        }
      }, 250);
    });
  }

  function captureAndBuildPdf(iframeDoc, recipeName) {
    var body = iframeDoc.body;
    var captureWidth = body.scrollWidth || 816;
    var captureHeight = body.scrollHeight || 1056;

    return window.html2canvas(body, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      logging: false,
      width: captureWidth,
      height: captureHeight,
      windowWidth: captureWidth,
      windowHeight: captureHeight
    }).then(function (canvas) {
      var jsPDF = window.jspdf.jsPDF;
      var pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: 'letter' });

      var pageWidth = 8.5;
      var pageHeight = 11;

      // Single-page case: content fits on one letter page
      var totalImgHeight = canvas.height * (pageWidth / canvas.width);
      if (totalImgHeight <= pageHeight + 0.05) {
        pdf.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          0, 0,
          pageWidth, totalImgHeight
        );
      } else {
        // Multi-page: slice the canvas into page-height chunks and
        // add each slice as a new page.
        var sliceHeightPx = Math.floor(canvas.width * (pageHeight / pageWidth));
        var yOffset = 0;
        var pageNum = 0;

        while (yOffset < canvas.height) {
          if (pageNum > 0) pdf.addPage();

          var thisSliceHeight = Math.min(sliceHeightPx, canvas.height - yOffset);

          var sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = thisSliceHeight;
          var ctx = sliceCanvas.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(canvas, 0, -yOffset);

          var sliceData = sliceCanvas.toDataURL('image/png');
          var sliceHeightInches = (thisSliceHeight / canvas.width) * pageWidth;
          pdf.addImage(sliceData, 'PNG', 0, 0, pageWidth, sliceHeightInches);

          yOffset += sliceHeightPx;
          pageNum++;
        }
      }

      var safeFileName = recipeName
        .replace(/[^a-zA-Z0-9 ]/g, '')
        .replace(/\s+/g, '-')
        .toLowerCase();
      pdf.save(safeFileName + '-recipe.pdf');
    });
  }

  // ============================================================
  // 12. Load jsPDF + autoTable on demand
  // ============================================================
  function loadJsPDF() {
    if (window.jspdf && window.jspdf.jsPDF) {
      try {
        var testDoc = new window.jspdf.jsPDF();
        if (typeof testDoc.autoTable === 'function') {
          return Promise.resolve();
        }
      } catch (e) { /* fall through and reload */ }
    }

    var jsPdfUrls = [
      'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js',
      'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'
    ];
    var autoTableUrls = [
      'https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.2/dist/jspdf.plugin.autotable.min.js',
      'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js'
    ];

    return loadScriptWithFallback(jsPdfUrls)
      .then(function () {
        if (!window.jspdf || !window.jspdf.jsPDF) {
          throw new Error('jsPDF did not register on window after script load');
        }
        return loadScriptWithFallback(autoTableUrls);
      })
      .then(function () {
        var testDoc = new window.jspdf.jsPDF();
        if (typeof testDoc.autoTable !== 'function') {
          throw new Error('autoTable did not attach to jsPDF after script load');
        }
        return true;
      })
      .catch(function (err) {
        console.error('[CAB] Failed to load jsPDF libraries:', err);
        alert('Could not load PDF libraries. Please check your internet connection and try again.');
        var libErr = new Error('LIBRARY_LOAD_FAILED');
        libErr.alreadyAlerted = true;
        throw libErr;
      });
  }

  function loadScriptWithFallback(urls) {
    var idx = 0;
    function tryNext() {
      if (idx >= urls.length) {
        return Promise.reject(new Error('All script URLs failed'));
      }
      var url = urls[idx++];
      return loadScript(url).catch(function (err) {
        console.warn('[CAB] Failed to load ' + url + ', trying next fallback...');
        return tryNext();
      });
    }
    return tryNext();
  }

  function loadHtml2Canvas() {
    if (window.html2canvas) return Promise.resolve();
    return loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js')
      .catch(function (err) {
        console.warn('[CAB] html2canvas failed to load:', err);
        return null;
      });
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  // ============================================================
  // 13. Utilities
  // ============================================================
  function val(id) {
    var el = document.getElementById(id);
    return el ? el.value : '';
  }
  function text(id) {
    var el = document.getElementById(id);
    return el ? el.textContent.trim() : '';
  }
  function getSelectText(id) {
    var el = document.getElementById(id);
    if (!el || el.selectedIndex < 0) return '';
    var opt = el.options[el.selectedIndex];
    return opt ? opt.text : '';
  }
  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
})();